import React from 'react';
import { Brain, Calendar, Eye, FileText } from 'lucide-react';
import { Modal } from '../../common/Modal';
import { getStatusColor, getPriorityColor } from '../parts/statusStyles';

// Extracted verbatim from JobManagement's JSX (Task 8).
// The orchestrator keeps the `show*` guard, so mount/unmount semantics are
// unchanged; this component is the modal's body only.
//
// isOpen is hardcoded true: the orchestrator's `showCustomerJobs &&` guard
// at the call site is what controls mounting. Because isOpen is never
// false, Modal's `if (!isOpen) return null` can never fire here — so
// dropping the call-site guard would not hide anything, it would leave a
// permanently mounted, permanently VISIBLE modal. The guard must stay.
const CustomerJobsModal = ({
  isLoading,
  setSelectedJob,
  setShowCustomerJobs,
  selectedCustomer,
  customerJobs,
  setActiveTab
}) => (
        <Modal
          isOpen
          onClose={() => setShowCustomerJobs(false)}
          title={`Jobs for ${selectedCustomer}`}
          size="xl"
        >
              {isLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
                  <span className="ml-3 text-ink-muted">Loading customer jobs...</span>
                </div>
              ) : customerJobs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-ink-muted mx-auto" />
                  <p className="mt-2 text-ink-muted">No jobs found for this customer.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {customerJobs.map((job) => (
                    <div key={job.id} className="border border-border-default rounded-lg p-4 hover:bg-surface-sunken transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 bg-accent/20 rounded-full flex items-center justify-center">
                                <FileText className="h-4 w-4 text-accent" />
                              </div>
                            </div>
                            <div>
                              <h4 className="text-lg font-medium text-ink">{job.job_number}</h4>
                              <p className="text-sm text-ink-muted">
                                {job.vehicle.make} {job.vehicle.model} {job.vehicle.year}
                                {job.vehicle.trim && ` ${job.vehicle.trim}`}
                              </p>
                            </div>
                          </div>
                          
                          <div className="ml-11 space-y-2">
                            <p className="text-sm text-ink">
                              <span className="font-medium">Symptoms:</span> {job.symptom_description}
                            </p>
                            
                            <div className="flex items-center space-x-4 text-sm">
                              <div className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1 text-ink-muted" />
                                <span className="text-ink-muted">Created:</span>
                                <span className="ml-1 font-medium text-ink">
                                  {new Date(job.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <span className={`inline-flex px-2 py-1 text-small font-medium rounded-full ${getStatusColor(job.job_status)}`}>
                                  {job.job_status.replace('_', ' ')}
                                </span>
                              </div>
                              <div className="flex items-center">
                                <span className={`inline-flex px-2 py-1 text-small font-medium rounded-full ${getPriorityColor(job.priority)}`}>
                                  {job.priority}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedJob(job);
                              setShowCustomerJobs(false);
                              setActiveTab('diagnostics');
                            }}
                            className="flex items-center px-3 py-1 bg-info-tint text-info rounded hover:bg-info-tint/80 transition-colors"
                          >
                            <Brain className="h-4 w-4 mr-1" />
                            Diagnose
                          </button>
                          <button className="flex items-center px-3 py-1 bg-surface-sunken text-ink rounded hover:bg-surface-sunken/80 transition-colors">
                            <Eye className="h-4 w-4 mr-1" />
                            Details
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
        </Modal>
);

export default CustomerJobsModal;
